import { api } from './client';
import type { Acquisition } from '../lib/tracking';

export type Language = 'Bg' | 'En';
export type SubscriptionTier = 'Free' | 'Pro';

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string | null;
  language: Language;
  emailConfirmed: boolean;
  /** A throwaway account seeded with sample data; the shell says so on every page. */
  isDemo: boolean;
  isAdmin: boolean;
  tier: SubscriptionTier;
  effectiveTier: SubscriptionTier;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  onboardingCompleted: boolean;
  /** Deadline reminder email. Off means the email footer's unsubscribe link was used, or Settings. */
  deadlineReminders: boolean;
  /** Health cover and ЗДДС threshold warnings. Its own switch. */
  complianceAlerts: boolean;
}

export const authApi = {
  register: (body: {
    email: string;
    password: string;
    displayName?: string | null;
    language: Language;
    acceptedTerms: boolean;
    /** Where the visitor came from — only ever set when they agreed to analytics. */
    acquisition?: Acquisition | null;
  }) => api<void>('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),

  login: (body: { email: string; password: string; rememberMe: boolean }) =>
    api<void>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),

  logout: () => api<void>('/api/auth/logout', { method: 'POST' }),

  /** Creates a throwaway account seeded with a plausible year and signs straight into it. */
  startDemo: (lang: 'bg' | 'en') =>
    api<void>(`/api/auth/demo?lang=${lang}`, { method: 'POST' }),

  me: () => api<CurrentUser>('/api/auth/me'),

  confirmEmail: (body: { userId: string; token: string }) =>
    api<void>('/api/auth/confirm-email', { method: 'POST', body: JSON.stringify(body) }),

  resendConfirmation: (email: string) =>
    api<void>('/api/auth/resend-confirmation', { method: 'POST', body: JSON.stringify({ email }) }),

  forgotPassword: (email: string) =>
    api<void>('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (body: { email: string; token: string; newPassword: string }) =>
    api<void>('/api/auth/reset-password', { method: 'POST', body: JSON.stringify(body) }),

  /** Full navigation, not fetch: the OAuth handshake needs a top-level redirect. */
  googleStartUrl: (returnUrl: string) =>
    `/api/auth/google/start?returnUrl=${encodeURIComponent(returnUrl)}`,
};
